import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Card,
  Typography,
  message,
  Spin,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { DeleteOutlined, PlusOutlined, MenuOutlined, UploadOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getFaqsContent, saveFaqsContent } from "../../redux/Faqs/FaqsActions";

/**
 * Brand-scoped FAQs editor. `brandToken` is the brand widget token (same as route :brandId).
 */
export default function Faqs({ brandToken }) {
  const userData = useSelector((state) => state.Auth.userData);
  const Faqsdata = useSelector((state) => state.FaqsReducer.Faqsdata);
  const isLoadingFaqs = useSelector((state) => state.FaqsReducer.isLoadingFaqs);
  const isLoadingSaveFaqs = useSelector((state) => state.FaqsReducer.isLoadingSaveFaqs);
  const usertoken = useSelector((state) => state.Auth.token);
  const [questions, setQuestions] = useState([{ id: Date.now(), text: "" }]);
  const [Content, setContent] = useState("");
  const dispatch = useDispatch();
  const { TextArea } = Input;

  useEffect(() => {
    if (!brandToken || !userData?.companyId) return;
    setQuestions([{ id: Date.now(), text: "" }]);
    setContent("");
    dispatch(
      getFaqsContent({
        companyId: userData.companyId,
        websiteToken: brandToken,
      })
    );
  }, [brandToken, userData?.companyId, dispatch]);

  useEffect(() => {
    if (Faqsdata == null || Faqsdata?.websiteToken !== brandToken) return;
    setContent(Faqsdata?.websiteContent ?? "");
    const qs = Faqsdata?.questions;
    if (Array.isArray(qs) && qs.length > 0) {
      setQuestions(qs);
    } else {
      setQuestions([{ id: Date.now(), text: "" }]);
    }
  }, [Faqsdata, brandToken]);

  const addQuestion = () => {
    setQuestions([...questions, { id: Date.now(), text: "" }]);
  };

  const removeQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const handleChange = (id, value) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, text: value } : q)));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const newItems = Array.from(questions);
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    setQuestions(newItems);
  };

  const onSave = () => {
    if (!brandToken) {
      message.error("Missing brand.");
      return;
    }
    const normalizedContent = (Content ?? "").trim();
    const normalizedQuestions = (questions ?? []).map((q) => (q?.text ?? "").trim());
    const isContentEmpty = normalizedContent.length === 0;
    const areAllQuestionsEmpty = normalizedQuestions.every((t) => t.length === 0);
    const areAllQuestionsFilled = normalizedQuestions.length > 0 && normalizedQuestions.every((t) => t.length > 0);

    // Allowed:
    // - both empty  -> save clears FAQs/context
    // - both filled -> save updates FAQs/context
    // Block:
    // - content filled but some/all questions empty
    // - questions filled but content empty
    const canSave = (isContentEmpty && areAllQuestionsEmpty) || (!isContentEmpty && areAllQuestionsFilled);
    if (!canSave) {
      if (isContentEmpty) {
        message.error("Please write website content if you want to save questions.");
      } else {
        message.error("Please fill all questions if you want to save website content.");
      }
      return;
    }
    dispatch(
      saveFaqsContent(
        {
          companyId: userData?.companyId,
          websiteToken: brandToken,
          questions,
          websiteContent: Content,
        },
        usertoken
      )
    );
    // const allFilled = questions.every((q) => q.text.trim() !== "");
    // if (Content === "") {
    //   message.error("Please write the content of the website for AI to answer precisely");
    // } else if (!allFilled) {
    //   message.error("Questions cannot be empty");
    // } else {

    // }
  };

  if (!brandToken) {
    return (
      <div style={{ padding: 16 }}>
        <Typography.Text type="secondary">Open FAQs from a brand (Widget settings).</Typography.Text>
      </div>
    );
  }

  if (isLoadingFaqs) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "center",
          flexDirection: "row",
          minHeight: 280,
        }}
      >
        <Spin size="large" tip="Loading FAQs..." />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <div style={{ padding: "0 0 12px 0" }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          FAQs &amp; website context
        </Typography.Title>
        <Typography.Text type="secondary">
          Content and questions apply to this brand&apos;s widget only.
        </Typography.Text>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          width: "100%",
          maxWidth: "100%",
          alignItems: "stretch",
        }}
      >
        <div>
          <Button type="primary" onClick={addQuestion} icon={<PlusOutlined />}>
            Add Question
          </Button>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="questions">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              >
                {questions.map((q, index) => (
                  <Draggable key={q.id} draggableId={String(q.id)} index={index}>
                    {(dragProvided) => {
                      const dndStyle = dragProvided.draggableProps.style;
                      return (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dndStyle,
                            width: "100%",
                            maxWidth: "100%",
                            marginBottom: 8,
                          }}
                        >
                          <Card
                            bodyStyle={{ display: "flex", alignItems: "center", width: "100%" }}
                            style={{ width: "100%", maxWidth: "100%" }}
                          >
                            <div {...dragProvided.dragHandleProps} style={{ cursor: "grab", flexShrink: 0 }}>
                              <MenuOutlined />
                            </div>
                            <Input
                              placeholder={`Question ${index + 1}`}
                              value={q.text}
                              onChange={(e) => handleChange(q.id, e.target.value)}
                              style={{ flex: 1, minWidth: 0, margin: "0 10px" }}
                            />
                            <Button icon={<DeleteOutlined />} danger onClick={() => removeQuestion(q.id)} style={{ flexShrink: 0 }} />
                          </Card>
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div style={{ width: "100%" }}>
          <Typography.Title level={5}>Website content</Typography.Title>
          <TextArea
            value={Content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            placeholder="Website content for AI to answer precisely..."
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 24, width: "100%" }}>
          <Button disabled={isLoadingSaveFaqs} type="primary" onClick={() => onSave()} icon={<UploadOutlined />}>
            {isLoadingSaveFaqs ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
